import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../utils/api';

export interface ConversationItem {
  id: string;
  customer: { name: string };
  assistant: { name: string };
  channel: string;
  status: string;
  lastMessage: string;
  lastActivity: string;
}

interface FetchParams {
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
}

interface ConversationsState {
  items: ConversationItem[];
  total: number;
  loading: boolean;
  error: string | null;
  filter: { search: string; status: string };
  page: number;
  pageSize: number;
}

const initialState: ConversationsState = {
  items: [],
  total: 0,
  loading: false,
  error: null,
  filter: { search: '', status: 'all' },
  page: 0,
  pageSize: 50,
};

export const fetchConversations = createAsyncThunk(
  'conversations/fetch',
  async (params: FetchParams = { limit: 50, offset: 0 }) => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    if (params.search) searchParams.set('search', params.search);
    if (params.status && params.status !== 'all') searchParams.set('status', params.status);
    const res = await api.get(`/conversations?${searchParams.toString()}`);
    return res.data;
  }
);

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<{ search?: string; status?: string }>) => {
      state.filter = { ...state.filter, ...action.payload } as any;
    },
    appendConversation: (state, action: PayloadAction<ConversationItem>) => {
      state.items = [action.payload, ...state.items];
      state.total += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload?.conversations || action.payload || [];
        state.total = action.payload?.total ?? state.items.length;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch conversations';
      });
  },
});

export const { setFilter, appendConversation } = conversationsSlice.actions;
export default conversationsSlice.reducer;


